import { useState } from 'react'
import Analytics from 'utils/googleAnalytics'
import { Select } from 'semantic-ui-react'

import { apiUrl } from 'environment'

import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'

import { HUMAN_GAME } from 'types/deck'

import { useActionless, useAppSelector } from 'redux/hooks'
import { TOGGLE_MODAL } from 'redux/active/actions/types'

import Modal from 'components/elements/Modal'
import PhatButton from 'components/formElements/PhatButton'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import PhatDropdown from 'components/formElements/PhatDropdown'

import { stringListToDropdown } from 'utils/DropdownConversions'

import styles from './addToCollection.module.scss'

type Props = {}

const AddToCollectionModal = ({}: Props) => {
  const categories = useAppSelector(state => state.deck.categories)
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const addToCollection = useAppSelector(state => state.active.modals.addToCollection)

  const [toggleModal] = useActionless(TOGGLE_MODAL)

  const [add, setAdd] = useState('Create')
  const [loading, setLoading] = useState(false)
  const [game, setGame] = useState(HUMAN_GAME.Paper)
  const [excludedCategories, setExcludedCategories] = useState(new Array<string>())

  const handleAddToCollection = () => {
    Analytics.signalEvent('Adding cards from a deck.')

    setLoading(true)

    const cardsToAdd = Object.values(cardMap)
      .filter(card => {
        if (excludedCategories.includes(card.categories[0] || '')) return false
        return true
      })
      .map(card => ({
        card: card.cardId,
        quantity: card.qty,
        foil: card.modifier === 'Foil',
        modifier: card.modifier,
        language: 1,
        condition: 1,
        tags: [],
        addOption: add,
        game: game,
      }))

    return RequestService.post(apiUrl + '/api/collection/create/', cardsToAdd)
      .then(() => {
        ToastService.create('Cards added to your collection', 'Collection Service', 'success')
        toggleModal({ modalName: 'addToCollection', flag: false })
      })
      .catch(() => ToastService.create('Unable to add cards to your collection', 'Collection', 'error'))
      .finally(() => setLoading(false))
  }

  return (
    <Modal
      closeButton
      open={addToCollection}
      onClose={() => toggleModal({ modalName: 'addToCollection', flag: false })}
      modalClassName={styles.modal}>
      <h3>Add to your collection</h3>
      <div className={styles.content}>
        <PhatDropdown
          className={styles.dropdown}
          value={game}
          options={Object.keys(HUMAN_GAME).map(gameName => ({
            label: gameName,
            id: `${HUMAN_GAME[gameName]}`,
            onClick: () => setGame(HUMAN_GAME[gameName]),
          }))}
        />
        <Select
          placeholder="Exclude Categories"
          name="categories"
          fluid
          multiple
          search
          selection
          options={stringListToDropdown(Object.keys(categories))}
          onChange={(_e: any, { value }: any) => setExcludedCategories(value)}
          value={excludedCategories}
        />
        <p>
          This will add all the cards in this deck to your personal collection. There is no way to undo this action
          other than manually removing the cards from your collection. Make sure to save if you've been editing as we
          will transfer the last saved version.
        </p>
        If we find a copy of a card already in your collection, we should&nbsp;
        <PhatDropdown
          options={[
            {
              id: 'Create',
              onClick: () => setAdd('Create'),
              label: 'add to the quantity of that card in your collection as a new record',
            },
            {
              id: 'Skip',
              onClick: () => setAdd('Skip'),
              label: 'skip over adding that card to your collection again',
            },
            {
              id: 'Update',
              onClick: () => setAdd('Update'),
              label: 'update the existing collection record if it is found',
            },
          ]}
          value={add}
        />
      </div>

      <div className={styles.controls}>
        <PhatButton color="green" onClick={handleAddToCollection} disabled={loading}>
          {!loading ? (
            'Add to my collection'
          ) : (
            <>
              <SimpleSpinner size="xSmall" /> Adding cards...
            </>
          )}
        </PhatButton>
      </div>
    </Modal>
  )
}

export default AddToCollectionModal
